import { useState, useCallback, lazy, Suspense } from "react";
import { Copy, Check } from "lucide-react";
import { useTranslation } from "react-i18next";

// Lazy-load SyntaxHighlighter - it's a huge library (~200KB)
const SyntaxHighlighter = lazy(() =>
  import("react-syntax-highlighter").then((mod) => ({ default: mod.Prism }))
);
const vscDarkPlusPromise = import("react-syntax-highlighter/dist/esm/styles/prism").then((m) => m.vscDarkPlus);

const langMap = {
  bash: "bash",
  javascript: "javascript",
  python: "python",
  json: "json",
  jsx: "jsx",
  typescript: "typescript",
  tsx: "tsx",
  html: "html",
  css: "css",
  yaml: "yaml",
  markdown: "markdown",
};

function CodeFallback({ code, language }) {
  return (
    <pre className="bg-[#1e1e1e] text-zinc-300 p-4 rounded-lg overflow-x-auto text-sm font-mono">
      <code>{code}</code>
    </pre>
  );
}

export default function CodeBlock({ code, language = "bash", showLineNumbers = false }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [style, setStyle] = useState(null);

  const onCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = code;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [code]);

  // Load style on mount
  if (!style) {
    vscDarkPlusPromise.then(setStyle);
  }

  const hlLang = langMap[language] || language;

  return (
    <div className="relative group">
      <button
        onClick={onCopy}
        className="absolute top-3 right-3 z-10 p-2 bg-zinc-800 hover:bg-zinc-700 rounded text-zinc-400 hover:text-white transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
        aria-label={t("code.copy", "Copy code")}
      >
        {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
      </button>
      <Suspense fallback={<CodeFallback code={code} language={language} />}>
        {style ? (
          <SyntaxHighlighter
            language={hlLang}
            style={style}
            showLineNumbers={showLineNumbers}
            customStyle={{
              background: "#1e1e1e",
              borderRadius: "0.5rem",
              padding: "1rem",
              fontSize: "0.875rem",
              margin: 0,
            }}
          >
            {code}
          </SyntaxHighlighter>
        ) : (
          <CodeFallback code={code} language={language} />
        )}
      </Suspense>
    </div>
  );
}
